import React, { useState, useEffect, useCallback } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { useAuth } from '../context/AuthContext';
import { txAPI, settingsAPI } from '../api';
import api from '../api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend);

const MONTHS = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];

export default function Reports() {
  const { currentFirm } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().toISOString().slice(0,7));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState('TRY');
  const [currencies, setCurrencies] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRoom, setFilterRoom] = useState('');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentFirm) return;
    settingsAPI.getCurrencies(currentFirm.id).then(r => setCurrencies(r.data));
    api.get(`/firms/${currentFirm.id}/rooms`).then(r => setRooms(r.data||[])).catch(()=>{});
    Promise.all([
      settingsAPI.getCategories(currentFirm.id),
      api.get(`/firms/${currentFirm.id}/transactions/categories`)
    ]).then(([catRes, txCatRes]) => {
      const sc = catRes.data;
      const sn = new Set(sc.map(c => c.name));
      const extra = txCatRes.data.filter(c => !sn.has(c.category)).map(c => ({ id:c.category, name:c.category, type:c.type }));
      setAllCategories([...sc, ...extra]);
    });
  }, [currentFirm]);

  const load = useCallback(async () => {
    if (!currentFirm) return;
    setLoading(true);
    try {
      const params = { period, display_currency: displayCurrency };
      if (filterCategory) params.category = filterCategory;
      if (filterType && filterType !== 'all') params.type = filterType;
      if (filterRoom) params.room = filterRoom;
      if (period === 'daily') { if (dateFrom && dateTo) { params.date_from = dateFrom; params.date_to = dateTo; } else params.month = month; }
      else if (period === 'monthly') { if (month) params.month = month; else params.year = year; }
      else params.year = year;
      const r = await txAPI.getSummary(currentFirm.id, params);
      setSummary(r.data);
    } finally { setLoading(false); }
  }, [currentFirm, period, year, month, dateFrom, dateTo, displayCurrency, filterCategory, filterType, filterRoom]);

  useEffect(() => { load(); }, [load]);

  const curSymbol = displayCurrency === 'TRY' ? '₺' : displayCurrency + ' ';
  const fmt = (n) => curSymbol + parseFloat(n||0).toLocaleString('tr-TR', {minimumFractionDigits:2,maximumFractionDigits:2});
  const years = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) years.push(y);

  const buildChartData = () => {
    if (!summary?.data) return null;
    const map = {};
    summary.data.forEach(r => {
      if (!map[r.period]) map[r.period] = { income:0, expense:0 };
      map[r.period][r.type] = parseFloat(r.total_display || r.total_try);
    });
    const periods = Object.keys(map).sort();
    let labels = periods;
    if (period === 'daily') labels = periods.map(p => p.slice(8));
    else if (period === 'monthly') labels = periods.map(p => MONTHS[parseInt(p.slice(5))-1]);
    return {
      labels,
      datasets: [
        { label:'Gelir', data: periods.map(p => Math.round(map[p]?.income||0)), backgroundColor:'#22c55e', borderRadius:3, barPercentage:0.5 },
        { label:'Gider', data: periods.map(p => Math.round(map[p]?.expense||0)), backgroundColor:'#ef4444', borderRadius:3, barPercentage:0.5 },
      ]
    };
  };

  const rawData = summary?.data || [];
  const totalInc = rawData.filter(r => r.type==='income').reduce((s,r) => s + parseFloat(r.total_display||r.total_try||0), 0);
  const totalExp = rawData.filter(r => r.type==='expense').reduce((s,r) => s + parseFloat(r.total_display||r.total_try||0), 0);
  const net = totalInc - totalExp;
  const cats = summary?.categories || [];
  const chartData = buildChartData();
  const catColors = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899','#94a3b8'];

  const openPDF = async () => {
    const fid = currentFirm?.id; if (!fid) return;
    const params = new URLSearchParams({ period, year, display_currency: displayCurrency });
    if (filterCategory) params.set('category', filterCategory);
    if (filterType && filterType !== 'all') params.set('type', filterType);
    if (filterRoom) params.set('room', filterRoom);
    if (period === 'daily') { if (dateFrom && dateTo) { params.set('date_from', dateFrom); params.set('date_to', dateTo); } else params.set('month', month); }
    else if (period === 'monthly' && month) params.set('month', month);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`/api/firms/${fid}/report?${params}`, { headers:{ 'Authorization':`Bearer ${token}` } });
      const html = await response.text();
      const w = window.open('', '_blank');
      w.document.write(html); w.document.close();
    } catch(e) { alert('PDF oluşturulamadı.'); }
  };

  const sel = { height:30, padding:'0 8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:12, background:'var(--bg2)', color:'var(--text)' };

  if (!currentFirm) return <div style={{padding:'2rem',color:'var(--text3)',textAlign:'center'}}>Lütfen bir firma seçin.</div>;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* Filtre */}
      <div className="card" style={{padding:'12px 16px'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <div style={{fontSize:11,color:'var(--text3)',fontWeight:500}}>Dönem:</div>
          {[['daily','Günlük'],['monthly','Aylık'],['yearly','Yıllık']].map(([v,l]) => (
            <button key={v} onClick={() => setPeriod(v)} className={`btn btn-sm ${period===v?'btn-dark':'btn-ghost'}`}>{l}</button>
          ))}
          <div style={{width:1,height:20,background:'var(--border)'}}/>
          {period==='daily' && <>
            <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setMonth('');}} style={{...sel,width:145}}/>
            <span style={{fontSize:12,color:'var(--text3)'}}>—</span>
            <input type="date" value={dateTo} onChange={e=>{setDateTo(e.target.value);setMonth('');}} style={{...sel,width:145}}/>
            <input type="month" value={month} onChange={e=>{setMonth(e.target.value);setDateFrom('');setDateTo('');}} style={{...sel,width:130}}/>
          </>}
          {period==='monthly' && <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{...sel,width:150}}/>}
          {period==='yearly' && <select value={year} onChange={e=>setYear(parseInt(e.target.value))} style={sel}>{years.map(y=><option key={y} value={y}>{y}</option>)}</select>}
          <div style={{width:1,height:20,background:'var(--border)'}}/>
          <select value={displayCurrency} onChange={e=>setDisplayCurrency(e.target.value)} style={sel}>
            {currencies.map(c=><option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          <div style={{width:1,height:20,background:'var(--border)'}}/>
          {[['all','Tümü'],['income','Gelir'],['expense','Gider']].map(([v,l]) => (
            <button key={v} onClick={()=>setFilterType(v)} className={`btn btn-sm ${filterType===v?'btn-dark':'btn-ghost'}`}>{l}</button>
          ))}
          <select value={filterCategory} onChange={e=>setFilterCategory(e.target.value)} style={{...sel,width:150}}>
            <option value="">Tüm Kategoriler</option>
            {allCategories.filter(c=>filterType==='all'||c.type===filterType).map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          {rooms.length>0 && <select value={filterRoom} onChange={e=>setFilterRoom(e.target.value)} style={{...sel,width:110}}>
            <option value="">Tüm Odalar</option>
            {rooms.map(r=><option key={r.id} value={r.name}>{r.name}</option>)}
          </select>}
          {(filterCategory||filterType!=='all'||filterRoom) && <button onClick={()=>{setFilterCategory('');setFilterType('all');setFilterRoom('');}} className="btn btn-ghost btn-sm">✕ Temizle</button>}
          <button onClick={openPDF} className="btn btn-sm" style={{marginLeft:'auto',background:'#14532d',color:'#4ade80',border:'none'}}>📄 PDF Al</button>
        </div>
      </div>

      {loading ? <div style={{textAlign:'center',padding:'3rem',color:'var(--text3)'}}>Yükleniyor...</div> : <>
        {/* Metrikler */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
          {[
            {label:'Toplam Gelir', value:fmt(totalInc), color:'var(--green)', border:'#22c55e'},
            {label:'Toplam Gider', value:fmt(totalExp), color:'var(--red)', border:'#ef4444'},
            {label:'Net', value:fmt(net), color:net>=0?'var(--green)':'var(--red)', border:net>=0?'#22c55e':'#ef4444'},
          ].map(m=>(
            <div key={m.label} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderTop:`2px solid ${m.border}`,borderRadius:'var(--radius-lg)',padding:'14px 16px'}}>
              <div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:6}}>{m.label}</div>
              <div style={{fontSize:22,fontWeight:600,color:m.color}}>{m.value}</div>
            </div>
          ))}
        </div>

        {/* Grafik */}
        {chartData && <div className="card">
          <div style={{fontSize:13,fontWeight:500,marginBottom:14}}>
            {period==='daily'?'Günlük':period==='monthly'?'Aylık':'Yıllık'} Gelir / Gider
          </div>
          <div style={{height:240}}>
            <Bar data={chartData} options={{
              responsive:true, maintainAspectRatio:false,
              plugins:{ legend:{ position:'top', labels:{ boxWidth:12, font:{size:11}, color:'#94a3b8' } } },
              scales:{
                y:{ ticks:{ callback:v=>curSymbol+Math.round(v).toLocaleString('tr-TR'), font:{size:11}, color:'#475569' }, grid:{ color:'#1e2535' } },
                x:{ ticks:{ font:{size:11}, color:'#475569' }, grid:{ color:'#1e2535' } }
              }
            }}/>
          </div>
        </div>}

        {/* Kategori */}
        {cats.length>0 && <div className="card">
          <div style={{fontSize:13,fontWeight:500,marginBottom:14}}>Kategori Dağılımı</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,alignItems:'center'}}>
            <div style={{height:200}}>
              <Doughnut data={{ labels:cats.map(c=>c.category||'Diğer'), datasets:[{ data:cats.map(c=>Math.round(c.total_display||c.total_try)), backgroundColor:catColors.slice(0,cats.length), borderWidth:0 }] }}
                options={{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false}}}}/>
            </div>
            <div>
              {cats.map((c,i)=>(
                <div key={c.category} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--border2)',fontSize:12}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <div style={{width:8,height:8,borderRadius:2,background:catColors[i]||'#ccc'}}/>
                    <span>{c.category||'Diğer'}</span>
                  </div>
                  <span style={{fontWeight:600,color:'var(--red)'}}>{fmt(c.total_display||c.total_try)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>}

        {/* Oda bazlı */}
        {summary?.byRoom?.length>0 && <div className="card">
          <div style={{fontSize:13,fontWeight:500,marginBottom:12}}>Oda bazlı gelir</div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{borderBottom:'1px solid var(--border)'}}>
              {['Oda','İşlem','Toplam'].map(h=><th key={h} style={{padding:'6px 0',fontSize:10,color:'var(--text3)',fontWeight:500,textAlign:'left',textTransform:'uppercase'}}>{h}</th>)}
            </tr></thead>
            <tbody>
              {summary.byRoom.map(r=>(
                <tr key={r.room} style={{borderBottom:'1px solid var(--border2)'}}>
                  <td style={{padding:'8px 0',fontWeight:500}}>{r.room}</td>
                  <td style={{padding:'8px 0',color:'var(--text3)'}}>{r.count}</td>
                  <td style={{padding:'8px 0',fontWeight:600,color:'var(--green)'}}>{fmt(r.total_display||r.total_try)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </>}
    </div>
  );
}
